import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { ProductDesignPageHeader, ProductDesignSection } from './ProductDesignDocPrimitives';
import { DataTable, type Column } from '../../components/ds';
import {
  TOKEN_GROUPS,
  CONSTANT_GROUPS,
  UNGROUPED_TOKENS,
  type TokenRow,
  type ConstantRow,
} from './designTokens.generated';

/**
 * Canonical token-system reference — LIGHT and DARK, GENERATED from src/index.css.
 *
 * Every value on this page comes from ./designTokens.generated.ts, which
 * scripts/buildDesignTokens.ts derives from index.css. `npm run
 * guard:design-tokens` fails when the two diverge.
 *
 * That indirection is not ceremony. This page previously hand-typed its values
 * and claimed to be "verified line-by-line against index.css" — and when the
 * light contrast ramp was darkened it kept publishing the old ones
 * (--text-pri #1e293b, --rule #e2e8f0, --text-dim #94a3b8, and three more),
 * while omitting sixteen tokens entirely, including every *-dim fill that
 * mono/primitives and ds/ resolve their tones to. A page that asserts it
 * matches a file must be derived from it, or the assertion decays the moment
 * someone edits the file.
 *
 * Note on rendering: the /priorities-ai-design-language shell is light-first
 * (daylight editorial). The swatches below use inline hex/rgba, so they render
 * true colors regardless — the DARK column shows real dark-mode values even on
 * the light page. The preview button re-asserts the full dark palette inline.
 */

// Compact solid-color swatch (hex only).
const Sw: React.FC<{ hex: string }> = ({ hex }) => (
  <div className="flex items-center gap-2">
    <span
      className="h-6 w-6 border border-[var(--rule-bright)] shrink-0"
      style={{ backgroundColor: hex }}
    />
    <code className="font-mono text-[13px] text-[var(--text-sec)]">{hex}</code>
  </div>
);

// Overlay tint preview — the rgba token layered over a representative base surface.
const Tint: React.FC<{ base: string; overlay: string; value: string }> = ({ base, overlay, value }) => (
  <div className="flex items-center gap-2">
    <span
      className="relative inline-block h-6 w-10 border border-[var(--rule-bright)] overflow-hidden shrink-0"
      style={{ backgroundColor: base }}
    >
      <span className="absolute inset-0" style={{ backgroundColor: overlay }} />
    </span>
    <code className="font-mono text-[13px] text-[var(--text-sec)]">{value}</code>
  </div>
);

/** rgba values are overlays and must be previewed over a surface, not as a chip. */
const isOverlay = (value: string): boolean => value.startsWith('rgba') || value.startsWith('hsla');

/**
 * Both tables on this page are ds/DataTable rather than hand-rolled markup.
 * A design-system reference that hand-rolls the thing it documents is the
 * clearest possible argument against itself — and these were two of the 145
 * remaining hand-rolled headers the ratchet counts.
 */
const TOKEN_COLUMNS: Column<TokenRow>[] = [
  {
    key: 'token',
    header: 'Token',
    width: '22%',
    render: (r) => <span className="whitespace-nowrap font-mono text-[var(--mustard)]">{r.token}</span>,
  },
  { key: 'role', header: 'Role', width: '34%' },
  {
    key: 'light',
    header: 'Light',
    render: (r) =>
      isOverlay(r.light) ? <Tint base="#F5F3EC" overlay={r.light} value={r.light} /> : <Sw hex={r.light} />,
  },
  {
    key: 'dark',
    header: 'Dark',
    render: (r) =>
      isOverlay(r.dark) ? <Tint base="#111827" overlay={r.dark} value={r.dark} /> : <Sw hex={r.dark} />,
  },
];

interface SurfaceRamp {
  surface: string;
  ramp: string;
  posture: React.ReactNode;
}

const SURFACE_RAMP: SurfaceRamp[] = [
  {
    surface: 'App — Elevation',
    ramp: 'charcoal-*',
    posture: (
      <>
        Light default, dark toggle (explicit <code className="font-mono text-[0.85em] text-[var(--mustard)]">dark:</code> variants)
      </>
    ),
  },
  {
    surface: 'App — Product',
    ramp: 'gray-*',
    posture: (
      <>
        Light or dark (auto-remap; never raw{' '}
        <code className="font-mono text-[0.85em] text-[var(--mustard)]">dark:bg-gray-*</code>)
      </>
    ),
  },
  {
    surface: 'Visual / Diagram',
    ramp: 'n/a',
    posture: (
      <>
        Dark (<code className="font-mono text-[0.85em] text-[var(--mustard)]">#0B0F19</code>) by default; light on warm paper (<code className="font-mono text-[0.85em] text-[var(--mustard)]">#FAFAF7</code>)
      </>
    ),
  },
];

const SURFACE_RAMP_COLUMNS: Column<SurfaceRamp>[] = [
  {
    key: 'surface',
    header: 'Surface',
    width: '24%',
    render: (r) => <span className="text-[var(--text-pri)]">{r.surface}</span>,
  },
  {
    key: 'ramp',
    header: 'Neutral ramp',
    width: '20%',
    render: (r) => <span className="font-mono text-[var(--mustard)]">{r.ramp}</span>,
  },
  { key: 'posture', header: 'Mode posture', render: (r) => r.posture },
];

const CONSTANT_COLUMNS: Column<ConstantRow>[] = [
  {
    key: 'token',
    header: 'Token',
    width: '22%',
    render: (r) => <span className="whitespace-nowrap font-mono text-[var(--mustard)]">{r.token}</span>,
  },
  { key: 'role', header: 'Role', width: '34%' },
  {
    key: 'value',
    header: 'Value',
    render: (r) => <span className="font-mono text-[13px] text-[var(--text-sec)]">{r.value}</span>,
  },
];

/**
 * Dark-mode values as inline custom properties, straight from the generated
 * token rows. The section shell is light-first, so a dark preview must
 * re-assert every token on a wrapper — deriving the list from the generated
 * rows keeps it from ever previewing a dark mode that no longer exists. The
 * `--p-*` entries point the page-scoped editorial vars back at the canonical
 * dark ramp, mirroring what `.dark .pd3` does in the section layout.
 */
const DARK_VARS = {
  ...Object.fromEntries(TOKEN_GROUPS.flatMap((g) => g.rows).map((r) => [r.token, r.dark])),
  '--p-paper': 'var(--bg)',
  '--p-shade': 'var(--surface)',
  '--p-cream': '#14120A',
  '--p-hairline': 'var(--rule)',
  '--p-hairline-2': 'var(--rule-bright)',
  '--p-ink': 'var(--text-pri)',
  '--p-ink-soft': 'var(--text-sec)',
  '--p-label': 'var(--text-ter)',
  '--p-faint': 'var(--text-dim)',
  '--p-gold': 'var(--mustard)',
  '--p-forest': 'var(--green)',
  '--p-clay': 'var(--coral)',
} as React.CSSProperties;

export const PrioritiesAITokenSystemPage: React.FC = () => {
  const [mode, setMode] = useState<'day' | 'night'>('day');
  return (
    <div
      style={mode === 'night' ? DARK_VARS : undefined}
      className={
        mode === 'night'
          ? 'bg-[var(--bg)] text-[var(--text-pri)] p-4 sm:p-6 -mx-1 transition-colors'
          : 'transition-colors'
      }
    >
      <div className="flex justify-end mb-6">
        <button
          type="button"
          onClick={() => setMode((m) => (m === 'day' ? 'night' : 'day'))}
          aria-pressed={mode === 'night'}
          className="inline-flex items-center border border-[var(--p-hairline-2)] px-3.5 py-2 font-mono text-[13px] uppercase tracking-[0.16em] text-[var(--p-label)] transition-colors hover:bg-[var(--hover-tint)] hover:text-[var(--p-ink)]"
        >
          {mode === 'day' ? 'Preview dark tokens' : 'Back to daylight'}
        </button>
      </div>
      <ProductDesignPageHeader
        title="Token system · light & dark"
        eyebrow="Foundations · source of truth"
        subtitle={
          <>
            The canonical token reference for the whole app, <strong className="text-[var(--text-pri)]">generated
            from</strong>{' '}
            <code className="font-mono text-[var(--mustard)]">src/index.css</code> — a gate fails if the two
            diverge, so “verified” is a fact here rather than a promise. There is{' '}
            <strong className="text-[var(--text-pri)]">one</strong> token system with two modes —
            light (default) and dark (toggle). Every value below shows both.
          </>
        }
      />

      {/* One system, two modes */}
      <ProductDesignSection
        title="One system, two modes"
        hint="Light is the default; dark is a toggle. Both are fully defined. The light palette is the same warm paper the public site uses."
      >
        <div className="space-y-3 text-sm text-[var(--text-sec)] leading-relaxed max-w-3xl">
          <p>
            <code className="font-mono text-[var(--mustard)]">:root</code> holds the dark palette (it also
            serves standalone diagram canvases). <code className="font-mono text-[var(--mustard)]">.dark</code>{' '}
            re-asserts dark tokens on any wrapper — not just <code className="font-mono text-[var(--mustard)]">&lt;html&gt;</code>{' '}
            — so <code className="font-mono text-[var(--mustard)]">var(--surface)</code> cascades correctly wherever{' '}
            <code className="font-mono text-[var(--mustard)]">.dark</code> lands.{' '}
            <code className="font-mono text-[var(--mustard)]">html:not(.dark)</code> remaps every token to its
            light value.
          </p>
          <p className="text-[var(--text-ter)]">
            This section (<code className="font-mono text-[var(--mustard)]">/priorities-ai-design-language</code>)
            renders in the daylight editorial shell — light-first, mapped onto the canonical dark ramp
            when the app theme is dark. The <strong className="text-[var(--text-pri)]">Dark</strong>{' '}
            columns below still render real dark-mode values via inline swatches, and the preview
            button above re-asserts the full dark palette in place.
          </p>
        </div>
      </ProductDesignSection>

      {/* Every colour token, generated from index.css */}
      {TOKEN_GROUPS.map((group) => (
        <ProductDesignSection key={group.id} title={group.title} hint={group.blurb}>
          <DataTable
            columns={TOKEN_COLUMNS}
            rows={group.rows}
            getRowKey={(r) => r.token}
            density="compact"
            bordered={false}
            caption={group.title}
            captionHidden
          />
        </ProductDesignSection>
      ))}

      {/* Mode-independent tokens */}
      {CONSTANT_GROUPS.map((group) => (
        <ProductDesignSection key={group.id} title={group.title} hint={group.blurb}>
          <DataTable
            columns={CONSTANT_COLUMNS}
            rows={group.rows}
            getRowKey={(r) => r.token}
            density="compact"
            bordered={false}
            caption={group.title}
            captionHidden
          />
        </ProductDesignSection>
      ))}

      {/* A non-empty list here means index.css gained a token the page would
          otherwise silently omit — which is exactly how it drifted before. */}
      {UNGROUPED_TOKENS.length > 0 && (
        <ProductDesignSection
          title="Ungrouped tokens"
          hint="These exist in index.css but no group above claims them, so they are undocumented. Add them to GROUPS in scripts/buildDesignTokens.ts."
        >
          <ul className="flex flex-wrap gap-2">
            {UNGROUPED_TOKENS.map((t) => (
              <li key={t} className="border border-[var(--coral)] px-2 py-1 font-mono text-[13px] text-[var(--coral)]">
                {t}
              </li>
            ))}
          </ul>
        </ProductDesignSection>
      )}

      {/* The two-mechanism rule — the real root cause */}
      <ProductDesignSection
        title="How dark mode is actually applied — the two mechanisms"
        hint="This is the root cause of the recurring 'hover too light in dark mode' bug. Know which mechanism you are relying on."
      >
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="border border-[var(--rule)] p-5">
            <h4 className="font-serif font-normal text-[var(--text-pri)] mb-2">1 · Auto-remap</h4>
            <p className="text-sm text-[var(--text-sec)] leading-relaxed">
              You write a <em>plain</em> light class like{' '}
              <code className="font-mono text-[var(--mustard)]">hover:bg-gray-100</code>. Unlayered{' '}
              <code className="font-mono text-[var(--mustard)]">.dark</code> rules in{' '}
              <code className="font-mono text-[var(--mustard)]">index.css</code> silently remap it to a token in dark.
              Covers <code className="font-mono text-[var(--mustard)]">gray / slate / zinc / neutral / stone / granite-gray</code>.
            </p>
          </div>
          <div className="border border-[var(--rule)] p-5">
            <h4 className="font-serif font-normal text-[var(--text-pri)] mb-2">2 · Explicit <code className="font-mono text-[var(--mustard)]">dark:</code> variant</h4>
            <p className="text-sm text-[var(--text-sec)] leading-relaxed">
              You write the dark value yourself:{' '}
              <code className="font-mono text-[var(--mustard)]">dark:bg-[var(--surface)]</code>,{' '}
              <code className="font-mono text-[var(--mustard)]">dark:text-charcoal-50</code>. Used by the Elevation
              primitives. You own the mapping — the auto-remap does not touch it.
            </p>
          </div>
        </div>

        <div className="border-l-[3px] border-[var(--p-clay)] bg-[var(--p-cream)] px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="text-sm text-[var(--text-sec)] leading-relaxed space-y-2">
              <p className="font-serif italic text-[var(--text-pri)]">The trap that caused the hover bug</p>
              <p>
                <code className="font-mono text-[var(--mustard)]">index.css</code> auto-remaps{' '}
                <em>plain</em> <code className="font-mono text-[var(--mustard)]">hover:bg-gray-*</code> — but it does{' '}
                <strong className="text-[var(--text-pri)]">not</strong> remap the explicit{' '}
                <code className="font-mono text-[var(--mustard)]">dark:hover:bg-gray-*</code> form. That slips through
                un-remapped and renders solid <code className="font-mono text-[var(--mustard)]">#1f2937</code>, which is{' '}
                <em>lighter</em> than the <code className="font-mono text-[var(--mustard)]">#111827</code> card it sits
                on — the washed-out hover.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 pt-1">
                <div className="flex items-start gap-2">
                  <X className="h-4 w-4 text-[var(--coral)] shrink-0 mt-0.5" />
                  <code className="font-mono text-[13px] text-[var(--text-sec)]">dark:hover:bg-gray-800</code>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-[var(--green)] shrink-0 mt-0.5" />
                  <code className="font-mono text-[13px] text-[var(--text-sec)]">hover:bg-gray-100</code>{' '}
                  <span className="text-[var(--text-ter)] text-[13px]">(auto-remapped)</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-[var(--green)] shrink-0 mt-0.5" />
                  <code className="font-mono text-[13px] text-[var(--text-sec)]">dark:hover:bg-[var(--hover-tint)]</code>{' '}
                  <span className="text-[var(--text-ter)] text-[13px]">(subtle rows)</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-[var(--green)] shrink-0 mt-0.5" />
                  <code className="font-mono text-[13px] text-[var(--text-sec)]">dark:hover:bg-[var(--surface-raised)]</code>{' '}
                  <span className="text-[var(--text-ter)] text-[13px]">(solid affordance)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ProductDesignSection>

      {/* Per-surface neutral ramp */}
      <ProductDesignSection title="Neutral ramp per surface" hint="Both ramps auto-remap in dark, so this is a convention, not a functional requirement — but keep each surface consistent.">
        <DataTable
          columns={SURFACE_RAMP_COLUMNS}
          rows={SURFACE_RAMP}
          getRowKey={(r) => r.surface}
          density="compact"
          bordered={false}
          caption="Neutral ramp per surface"
          captionHidden
        />
      </ProductDesignSection>

      <ProductDesignSection title="Related references" hint="The deeper canonical pages this sheet feeds.">
        <div className="flex flex-wrap gap-x-8 gap-y-3 font-body text-[13px]">
          <Link to="colors" className="text-[var(--p-gold)] underline-offset-4 hover:underline">Colors →</Link>
          <Link to="typography" className="text-[var(--p-gold)] underline-offset-4 hover:underline">Typography →</Link>
          <Link to="/elevation/design-language" className="text-[var(--p-gold)] underline-offset-4 hover:underline">Design language →</Link>
        </div>
      </ProductDesignSection>
    </div>
  );
};
